import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { FeatherCheck, FeatherLoader, FeatherX } from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { CheckSubmitFooter } from "@/components/checks/CheckSubmitFooter";
import type { PassFailValue } from "@/components/checks/PassFailControl";
import { PassFailControl } from "@/components/checks/PassFailControl";
import { RegisterEmptyState } from "@/components/logs/RegisterEmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useListFireDoorsQueryControllerList } from "@/generated/fire-doors/fire-doors";
import type { UseOfflineLogEntriesResult } from "@/hooks/use-offline-log-entries";
import { useOfflineRegister } from "@/hooks/use-offline-register";
import {
  FIRE_DOOR_INSPECTION_RESULT_FIELD,
  fireDoorLabel,
  isFireDoorInspectionPass,
} from "@/lib/fire-alarm-tests";
import { memberDisplayName } from "@/lib/member-display";
import { canRaiseFollowUp } from "@/lib/roles";

type FireDoorInspectionEntry = UseOfflineLogEntriesResult["entries"][number];

interface FireDoorInspectionViewProps {
  logTypeId: string;
  logTypeName: string;
  entries: FireDoorInspectionEntry[];
  isLoading: boolean;
  isOffline: boolean;
  createEntry: UseOfflineLogEntriesResult["createEntry"];
}

/**
 * A fire door inspection (NAU-441): one registered door per inspection, picked
 * from the nursery's active doors, plus a single pass/fail.
 */
export default function FireDoorInspectionView({
  logTypeId,
  logTypeName,
  entries,
  isLoading: entriesLoading,
  isOffline,
  createEntry,
}: FireDoorInspectionViewProps) {
  const navigate = useNavigate();
  const { isPlatformAdmin, userRoleType } = useAuth();
  const { selectedNurseryId, nurseryRole } = useNursery();
  // Only managers and group admins may create follow-ups, so everyone else
  // gets plain Submit rather than a button that saves and then 403s.
  const canRaise = canRaiseFollowUp({
    isPlatformAdmin,
    userRoleType,
    nurseryRole,
  });

  const doorsQuery = useListFireDoorsQueryControllerList(
    selectedNurseryId ?? "",
    {},
    // Always the current register, so a retired door can't be picked.
    { query: { enabled: !!selectedNurseryId, refetchOnMount: "always" } },
  );
  const {
    items: doors,
    isLoading,
    isServedFromCache,
  } = useOfflineRegister("fire-doors", selectedNurseryId, doorsQuery);
  // Offline and never loaded on this device: "none registered" would be untrue.
  const isRegisterUnavailable =
    isOffline && !doorsQuery.data && !isServedFromCache;
  const doorsById = useMemo(
    () => new Map(doors.map((door) => [door.id, door])),
    [doors],
  );

  const [pickedDoorId, setDoorId] = useState<string>("");
  const [result, setResult] = useState<PassFailValue>(undefined);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One retired since it was picked drops out of the register: back to the
  // placeholder rather than a blank picker.
  const doorId = doorsById.has(pickedDoorId) ? pickedDoorId : "";

  const canSubmit = !!doorId && result !== undefined;

  const handleSubmit = async (raiseFollowUp: boolean) => {
    if (!canSubmit) return;
    if (isSubmitting) return;
    const door = doorsById.get(doorId);
    setIsSubmitting(true);
    try {
      const { outcome, error, serverId } = await createEntry(
        {
          logTypeId,
          data: { [FIRE_DOOR_INSPECTION_RESULT_FIELD]: result === "pass" },
          notes: notes.trim() || undefined,
          fireDoorId: doorId,
        },
        { awaitSync: true },
      );
      if (outcome === "failed") {
        // Keep the form filled so the corrected retry needs no re-entry. The
        // usual cause is a door retired since the list loaded.
        toast.error(
          error ?? "Failed to record the inspection — please try again",
        );
        void doorsQuery.refetch();
        return;
      }
      toast.success(
        outcome === "queued"
          ? "Inspection saved — it will sync when you're back online"
          : "Inspection recorded",
      );
      setDoorId("");
      setResult(undefined);
      setNotes("");

      if (!raiseFollowUp) return;
      // A queued inspection has no server id yet, so there is nothing to
      // attach a follow-up to.
      if (outcome !== "synced") {
        toast.info(
          "Raise the follow-up from the log history once the inspection has synced — it has no server record yet.",
        );
        return;
      }
      if (!serverId) {
        toast.info(
          "Inspection saved. Raise the follow-up from the log history.",
        );
        return;
      }
      navigate({
        to: "/follow-up-actions/create",
        search: {
          nurseryId: selectedNurseryId ?? "",
          scheduledQuestionnaireId: "",
          answerId: "",
          questionText: "",
          checklistName: "",
          logEntryId: serverId,
          logEntryContext: door
            ? `${logTypeName} — ${fireDoorLabel(door)}`
            : logTypeName,
          fromSubmission: true,
        },
      });
    } catch {
      toast.error("Failed to record the inspection — please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (doors.length === 0) {
    return (
      <RegisterEmptyState
        isRegisterUnavailable={isRegisterUnavailable}
        emptyMessage="No fire doors are registered for this nursery yet. Ask a group admin to add them in the nursery's settings."
      />
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {isOffline && (
        <div className="flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <span className="text-body font-body text-default-font">
            You&apos;re offline. This inspection will sync when you reconnect.
          </span>
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-neutral-200 p-4">
        {/* Subframe's Select label is a plain span, not tied to its trigger;
            a wrapping <label> gives the trigger button its accessible name. */}
        <label className="flex w-full flex-col items-start gap-3">
          <span className="text-body-bold font-body-bold text-default-font">
            Fire door inspected
          </span>

          <Select
            className="w-full"
            placeholder="Select the door you inspected"
            value={doorId}
            onValueChange={setDoorId}
            disabled={isSubmitting}
          >
            {doors.map((door) => (
              <Select.Item key={door.id} value={door.id}>
                {fireDoorLabel(door)}
              </Select.Item>
            ))}
          </Select>
        </label>

        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-body font-body text-default-font">
            Does the door close fully, seal and is it unobstructed?
          </span>
          <PassFailControl
            label="Door OK"
            value={result}
            onChange={setResult}
            disabled={isSubmitting}
          />
        </div>

        <TextArea className="h-auto w-full" label="Notes (optional)">
          <TextArea.Input
            placeholder="Note anything that needs attention"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </TextArea>

        <CheckSubmitFooter
          onSubmit={() => void handleSubmit(false)}
          onSubmitAndRaiseFollowUp={
            canRaise ? () => void handleSubmit(true) : undefined
          }
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
          hasFailure={result === "fail"}
          submitLabel="Record inspection"
        />
      </div>

      <div className="flex w-full flex-col gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Today&apos;s inspections
        </span>

        {entriesLoading ? (
          <div className="flex w-full items-center justify-center py-8">
            <Loader size="medium" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-1 py-8">
            <span className="text-body font-body text-subtext-color">
              No inspections recorded yet
            </span>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-2">
            {entries.map((entry) => (
              <FireDoorInspectionEntryRow
                key={entry.id}
                entry={entry}
                doorLabel={entryDoorLabel(entry, doorsById)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A queued offline entry only knows its door's id, so the name comes from the
 * register; an inspection from before doors were registered shows its room.
 */
function entryDoorLabel(
  entry: FireDoorInspectionEntry,
  doorsById: Map<string, { name: string; room: { name: string } | null }>,
): string {
  if (entry.fireDoor?.name) return fireDoorLabel(entry.fireDoor);
  const registered = entry.fireDoor
    ? doorsById.get(entry.fireDoor.id)
    : undefined;
  if (registered) return fireDoorLabel(registered);
  return entry.room?.name ?? "";
}

function FireDoorInspectionEntryRow({
  entry,
  doorLabel,
}: {
  entry: FireDoorInspectionEntry;
  doorLabel: string;
}) {
  const passed = isFireDoorInspectionPass(
    entry.data as Record<string, unknown>,
  );
  const isPending = entry.localSyncState === "pending";

  return (
    <div
      className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 ${
        passed ? "border-neutral-200 bg-neutral-50" : "border-red-200 bg-red-50"
      }`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          passed ? "bg-green-100" : "bg-red-100"
        }`}
      >
        {passed ? (
          <FeatherCheck className="h-4 w-4 text-green-600" />
        ) : (
          <FeatherX className="h-4 w-4 text-red-600" />
        )}
      </div>
      <div className="flex grow flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {format(new Date(entry.timestamp), "HH:mm")}
          </span>
          <span
            className={`text-caption-bold font-caption-bold ${passed ? "text-green-600" : "text-red-600"}`}
          >
            {passed ? "Pass" : "Fail"}
          </span>
          {isPending && (
            <FeatherLoader className="h-3 w-3 animate-spin text-subtext-color" />
          )}
        </div>
        <span className="text-caption font-caption text-subtext-color">
          {doorLabel}
        </span>
        {entry.notes && (
          <span className="text-caption font-caption text-subtext-color italic">
            {entry.notes}
          </span>
        )}
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {memberDisplayName(entry.recordedBy)}
      </span>
    </div>
  );
}
